/**
 * Minutes in, something a person would say out loud.
 *
 * The API speaks minutes, which is right for a stored target and wrong for a
 * field somebody types into: nobody decides a furnishing lead should be
 * answered "within 1440". The screen edits a number and a unit and converts
 * here, so the contract stays in one unit and the form stays in the user's.
 */

export type DurationUnit = 'minutes' | 'hours';

/**
 * The unit the value reads most naturally in.
 *
 * Whole hours are shown as hours; anything else stays in minutes rather than
 * being rounded into "1.25 hours", which loses the precision somebody chose.
 */
export const unitFor = (minutes: number): DurationUnit =>
  minutes >= 60 && minutes % 60 === 0 ? 'hours' : 'minutes';

export const toUnit = (minutes: number, unit: DurationUnit): number =>
  unit === 'hours' ? minutes / 60 : minutes;

export const toMinutes = (value: number, unit: DurationUnit): number =>
  Math.round(unit === 'hours' ? value * 60 : value);

/**
 * A target as a sentence fragment: "4 hours", "15 minutes", "1 day".
 *
 * Days earn a case of their own because "24 hours" and "a working day" are
 * the same number and only one of them is how the target was described when
 * it was chosen.
 */
export function humaniseTarget(minutes: number): string {
  if (!isFinite(minutes) || minutes <= 0) return '—';

  if (minutes % (24 * 60) === 0) {
    const days: number = minutes / (24 * 60);
    return days === 1 ? '1 day' : `${days} days`;
  }

  if (minutes % 60 === 0) {
    const hours: number = minutes / 60;
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }

  if (minutes < 60) return minutes === 1 ? '1 minute' : `${minutes} minutes`;

  const hours: number = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

/** Matches the lead service's guard-rails, so the form fails before the API does. */
export const MIN_TARGET_MINUTES = 1;
export const MAX_TARGET_MINUTES = 7 * 24 * 60;

export const targetError = (minutes: number): string | undefined => {
  if (!isFinite(minutes) || !Number.isInteger(minutes)) return 'Enter a whole number';
  if (minutes < MIN_TARGET_MINUTES) return 'Must be at least 1 minute';
  if (minutes > MAX_TARGET_MINUTES) return 'Cannot be longer than 7 days';
  return undefined;
};

/**
 * The at-risk threshold is a ratio on the wire and a percentage on the screen.
 *
 * 0.1–0.99 on the service side: it has to warn after the lead arrives and
 * before the breach, and a threshold of 100% warns at the moment of the
 * breach, which is not a warning.
 */
export const ratioError = (percent: number): string | undefined => {
  if (!isFinite(percent) || !Number.isInteger(percent)) return 'Enter a whole number';
  if (percent < 10 || percent > 99) return 'Must be between 10% and 99%';
  return undefined;
};
